import { db } from '../src/firebase.js';
import { 
    doc, 
    getDoc, 
    collection, 
    getDocs, 
    query, 
    where, 
    orderBy, 
    limit, 
    startAfter 
} from 'firebase/firestore';

/**
 * Fetches a single product by ID including the 'stock' object field.
 */
export async function getProduct(productId) {
    try {
        const docRef = doc(db, 'products', productId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() };
        }
        return null;
    } catch (error) {
        console.error(`[productService] Error fetching product ${productId}:`, error);
        throw error;
    }
}

/**
 * Fetches a paginated batch of products from Firestore with filters and sorting.
 * 
 * @param {Object} filters - { category, parentCategory, collection, motion, minPrice, maxPrice, size }
 * @param {string} sortBy - Sort key (featured, newest, price-asc, price-desc)
 * @param {DocumentSnapshot} lastDoc - The cursor for pagination
 * @param {number} pageSize - Number of items to fetch
 * @returns {Promise<{products: Array, lastDoc: DocumentSnapshot, hasMore: boolean}>}
 */
export async function getProductsPage(filters = {}, sortBy = 'featured', lastDoc = null, pageSize = 12) {
    try {
        let q = collection(db, 'products');
        let constraints = [];

        // Detect if any filtering or specific sorting is active
        const hasActiveFilter = !!(filters.parentCategory || filters.category || filters.collection || filters.size || 
                                 (filters.categories && filters.categories.length > 0));
        const hasActiveSort = sortBy !== 'featured';

        // Only apply constraints if a selection has been made. 
        // If no filters are active and sort is 'Featured', we perform a plain query with nothing but the limit.
        if (hasActiveFilter || hasActiveSort) {
            // 1. Apply Filters
            if (filters.parentCategory) {
                constraints.push(where('parentCategory', '==', filters.parentCategory));
            }
            if (filters.category) {
                constraints.push(where('collections', 'array-contains', filters.category));
            }
            if (filters.categories && filters.categories.length > 0) {
                constraints.push(where('category', 'in', filters.categories));
            }
            if (filters.collection) {
                constraints.push(where('collection', '==', filters.collection));
            }
            if (filters.size) {
                constraints.push(where('sizes', 'array-contains', filters.size));
            }

            // 2. Apply Sorting
            if (sortBy === 'price-asc') {
                constraints.push(orderBy('priceValue', 'asc'));
            } else if (sortBy === 'price-desc') {
                constraints.push(orderBy('priceValue', 'desc'));
            } else if (sortBy === 'newest') {
                constraints.push(orderBy('publishedAt', 'desc'));
            }
        }

        // 3. Apply Pagination
        if (lastDoc) constraints.push(startAfter(lastDoc));
        constraints.push(limit(pageSize));

        const querySnapshot = await getDocs(query(q, ...constraints));
        
        const products = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        return {
            products,
            lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
            hasMore: querySnapshot.docs.length === pageSize
        };
    } catch (error) {
        console.error("[productService] Firestore Fetch Error:", error);
        console.log("Failed Query Config:", { filters, sortBy, lastDoc });
        
        if (error.message && error.message.includes('index')) {
            console.warn("TIP: This specific combination of filters and sorting likely requires a composite index in the Firebase Console.");
        }
        
        throw error; // Let Shop.js catch this to trigger the fallback logic
    }
}

/**
 * Fetches related products based on category and parentCategory.
 */
export async function getRelatedProducts(category, parentCategory, currentProductId) {
    try {
        const productsRef = collection(db, 'products');
        
        // 1. Fetch by Category
        const qCat = query(productsRef, where('category', '==', category), limit(10));
        const snapCat = await getDocs(qCat);
        let results = snapCat.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(p => p.id !== currentProductId);

        // 2. If we need more, fetch by parentCategory
        if (results.length < 4) {
            const qParent = query(productsRef, where('parentCategory', '==', parentCategory), limit(10));
            const snapParent = await getDocs(qParent);
            const parentResults = snapParent.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(p => 
                    p.id !== currentProductId && 
                    !results.some(existing => existing.id === p.id)
                );
            
            results = [...results, ...parentResults];
        }

        return results.slice(0, 4);
    } catch (error) {
        console.error("[productService] Error fetching related products:", error);
        return [];
    }
}